import { ISalesAccountDefaults } from "models/salesAccount/salesAccount.model";
import { ExceptionsQueueRowDto } from "services/api/shipments/dtos/exceptions-dashboard.response.dto";
import { ThemeColorPalettes } from "theme/theme-colors.theme";

export type TabKey = "action" | "silent" | "returns" | "undelivered";

export const EXCEPTIONS_FEEDBACK_EMAIL = "jlei@itdglobal.com";

// Whether a sales account's own (non-staff) users can see the Exceptions
// page: the FFAST "Show Exceptions page" toggle (General Connexx Settings),
// which lands in defaultSettings.showExceptionsPage. The server's exceptions
// guard (itd-sales-admin.guard.ts) enforces the same rule on the API.
// settingsSlice.settings holds the logged-in account's defaultSettings (null
// while still loading, so access appears once settings arrive).
export const canSalesAccountSeeExceptions = (
  defaultSettings: Pick<ISalesAccountDefaults, "showExceptionsPage"> | null
): boolean => defaultSettings?.showExceptionsPage === true;

// CHART-ONLY hex pairs (the recharts trends card). Everything outside a chart
// uses theme palette tokens (theme.colors[palette][tint]) instead — matching
// the Intelligence page, where charts keep hand-validated hexes but all other
// chrome is themed.
//
// Palette pairs validated for both surfaces (dataviz six-checks): 600-level
// text/marks on light, 500-level on dark (amber stays 600 — 500 sits outside
// the dark lightness band). "review" is a deliberate neutral gray — it means
// "not yet classified", is always accompanied by its label, and fails the
// chroma floor by design.
type ModePair = { light: string; dark: string };
export const pick = (pair: ModePair, isDark: boolean) =>
  isDark ? pair.dark : pair.light;

export const STATUS_COLORS: Record<
  "red" | "amber" | "blue" | "gray" | "teal" | "green",
  ModePair
> = {
  red: { light: "#dc2626", dark: "#ef4444" },
  amber: { light: "#d97706", dark: "#d97706" },
  blue: { light: "#2563eb", dark: "#3b82f6" },
  gray: { light: "#64748b", dark: "#94a3b8" },
  teal: { light: "#0d9488", dark: "#14b8a6" },
  green: { light: "#059669", dark: "#10b981" },
};

// Who can act on an exception — drives the group cards, the reason chips and
// the trends chart. Copy stays seller-voiced: sellers may eventually see this
// page. `chartColor` feeds only the recharts bars/legend; every non-chart
// surface (chips, tiles, colored text) uses `palette` theme tokens. The theme
// has no amber/teal palettes and its yellow scale is a gray placeholder, so
// amber maps to orange.
export const OWNER_META: Record<
  ExceptionsQueueRowDto["owner"],
  {
    title: string;
    sub: string;
    chartColor: ModePair;
    palette: ThemeColorPalettes;
  }
> = {
  you: {
    title: "You can fix",
    sub: "Missing info you can supply: address, customs docs, ID, packaging",
    chartColor: STATUS_COLORS.red,
    palette: "red",
  },
  buyer: {
    title: "Contact your buyer",
    sub: "Refused, moved, on holiday, not collected",
    chartColor: STATUS_COLORS.amber,
    palette: "orange",
  },
  carrier: {
    title: "Carrier to chase",
    sub: "Misrouted, damaged, delayed or lost: raise with the carrier",
    chartColor: STATUS_COLORS.blue,
    palette: "blue",
  },
  review: {
    title: "Needs classification",
    sub: "Exception reported, reason not yet provided by the carrier",
    chartColor: STATUS_COLORS.gray,
    palette: "gray",
  },
};

export const OWNER_KEYS = Object.keys(
  OWNER_META
) as ExceptionsQueueRowDto["owner"][];

export const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });

export const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
